import dayjs from 'dayjs';
import { getPointOfSaleColor } from './point-of-sale-colors';

/**
 * Determina se un dato momento (data/ora) è considerato un orario lavorativo e restituisce le relative informazioni
 *
 * @param data - La data/ora da verificare
 * @param type - Tipo di visualizzazione (es. "monthCells" per vista mensile)
 * @param calendarPerUser - Oggetto contenente orari di lavoro regolari per utente
 * @param shiftPerUser - Oggetto contenente turni di lavoro per utente
 * @returns Un array contenente [ID posizione, colore/classe CSS, codice stato (0=chiuso, 1=turno, 2=orario lavoro)]
 */
export const isOpen = (
  data: Date | undefined,
  type?: string,
  calendarPerUser?: any,
  shiftPerUser?: any
) => {
  // Se non ci sono dati di calendario o turni, restituisce subito stato "chiuso"
  if (!calendarPerUser && !shiftPerUser) return ['', 'var(--closed-color)', 0];

  // Estrae l'ora e la data dal parametro in formato stringa
  const time = dayjs(data).format('HH:mm:ss');
  const date = dayjs(data).format('YYYY-MM-DD');

  // Inizializzazione variabili
  let color = 'var(--closed-color)'; // Colore predefinito (chiuso)
  let posId = ''; // ID della posizione (per calendari multipli)
  let i = 0; // Indice per scorrere le chiavi
  let temp: any; // Dati temporanei del calendario
  let tempShift: any; // Dati temporanei dei turni
  let code = 0; // Codice stato (0=chiuso, 1=turno, 2=orario lavoro)

  // Crea un array con tutte le chiavi uniche da calendarPerUser e shiftPerUser
  const union = [
    ...new Set([
      ...(calendarPerUser?.keys ?? []),
      ...(shiftPerUser?.keys ?? []),
    ]),
  ];

  /**
   * Verifica se l'orario corrente rientra in uno dei due intervalli di tempo definiti
   * @param calendar - Oggetto calendario con startTime1, endTime1, startTime2, endTime2
   * @returns true se l'orario rientra in uno degli intervalli, false altrimenti
   */
  const check = (calendar: any) =>
    calendar &&
    ((time >= calendar.startTime1 && time < calendar.endTime1) ||
      (time >= calendar.startTime2 && time < calendar.endTime2));

  // Scorre tutte le chiavi finché non trova un match o esaurisce le chiavi
  while (i < union.length && color === 'var(--closed-color)') {
    const key = union[i];
    let dates;
    let shiftDates;

    // Estrae i dati del calendario per la chiave e data correnti
    if (
      calendarPerUser &&
      calendarPerUser?.values &&
      key in calendarPerUser.values
    ) {
      dates = calendarPerUser.values[key];
      temp = dates !== undefined && date in dates ? dates[date] : undefined;
    }

    // Estrae i dati dei turni per la chiave e data correnti
    if (shiftPerUser && shiftPerUser?.values && key in shiftPerUser.values) {
      shiftDates = shiftPerUser.values[key];
      tempShift =
        shiftDates !== undefined && date in shiftDates
          ? shiftDates[date]
          : undefined;
    }

    // LOGICA DIFFERENZIATA PER TIPO DI VISUALIZZAZIONE
    if (type && type === 'monthCells') {
      // Per vista mensile: verifica solo la presenza di dati (non l'orario specifico)
      if (temp) {
        color = getPointOfSaleColor(key, 'calendar'); // Usa il colore dell'orario di lavoro
        code = 2; // Codice 2 = orario di lavoro
      } else if (tempShift) {
        color = getPointOfSaleColor(key, 'shift'); // Usa il colore del turno
        code = 1; // Codice 1 = turno
      } else {
        color = 'var(--closed-color)'; // Colore chiuso
        code = 0; // Codice 0 = chiuso
      }
    } else {
      // Per vista normale: controlla che l'orario specifico rientri negli intervalli
      const checkCalendar = check(temp); // Verifica se è orario di lavoro
      const checkShift = check(tempShift); // Verifica se è turno

      if (checkCalendar) {
        posId = key; // Salva l'ID della posizione
        color = getPointOfSaleColor(key, 'calendar'); // Usa il colore dell'orario di lavoro
        code = 2; // Codice 2 = orario di lavoro
      } else if (checkShift) {
        posId = key; // Salva l'ID della posizione
        color = getPointOfSaleColor(key, 'shift'); // Usa il colore del turno
        code = 1; // Codice 1 = turno
      } else {
        posId = ''; // Nessuna posizione
        color = 'var(--closed-color)'; // Colore chiuso
        code = 0; // Codice 0 = chiuso
      }
    }

    // Resetta i valori temporanei per la prossima iterazione
    temp = undefined;
    tempShift = undefined;
    i++;
  }

  // Restituzione del risultato in base al contesto
  if (calendarPerUser?.multi) return [posId, color, code];
  // Per calendari multipli, restituisce anche posId
  else if (code === 0) return ['', 'var(--closed-color)', code]; // Per stato chiuso, usa il colore predefinito

  // Per altri stati, restituisce classe CSS appropriata
  return [
    '',
    code === 1
      ? 'var(--alt-background-color)' // Per turni
      : 'var(--base-foreground-color)', // Per orari di lavoro
    code,
  ];
};
